/**
 * RollingNumber — a car-odometer style animated number (task #3945).
 *
 * While a quote is being (re)fetched (`loading`) the digits churn/roll in
 * place of a spinner; when the value resolves they settle (~480ms) onto the
 * new figure. Currency / locale formatting is preserved verbatim because the
 * caller supplies the `format` function — we only animate the glyphs of the
 * already-formatted string.
 *
 * Guarantees:
 *  - Honors `prefers-reduced-motion`: falls back to the existing loading
 *    indicator (`fallback`) while loading and a plain formatted value when
 *    settled — no motion at all.
 *  - Retargets on rapid back-to-back fetches (only the latest value settles).
 *  - Handles fetch failure / abort cleanly: when `loading` drops without a
 *    finite value it stops churning (no infinite spin) and shows the last
 *    settled value (or `fallback`).
 *  - Churns from a neutral placeholder on the very first load.
 *  - No layout shift: tabular figures keep every digit the same width.
 *
 * IMPORTANT: in the idle / settled state the component renders the plain
 * formatted string as its only text node, so `data-testid` text assertions
 * and screen-readers see exactly the formatted value. The reels (which would
 * otherwise pollute `textContent` with `0123456789`) only mount while an
 * animation is actually running at runtime — never on first paint or in
 * reduced-motion / SSR.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { forwardSettleTarget, nearestSettleTarget } from "./rolling-number-math";

const SETTLE_MS = 480;
const STAGGER_MS = 55;
const CHURN_SPEED = 11; // digit-rows per second while churning

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(prefersReducedMotion);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Numeric typography shared by EVERY phase (idle, reduced-motion, churn,
 * settle). Tabular figures keep all glyphs the same fixed width and `nowrap`
 * prevents wrapping, so the formatted string occupies an identical box whether
 * it is animating or at rest — eliminating the horizontal "bump" that used to
 * appear the moment the odometer settled (proportional → tabular figure jump).
 */
const NUMERIC_STYLE = {
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
} as const;

/**
 * How a reel settles onto its target digit:
 *  - "forward": strictly upward (price total) — winds forward, possibly almost a
 *    full turn, never reversing. Mounts at offset 0.
 *  - "nearest": shortest hop from the PREVIOUS digit (live countdown) — a single
 *    gentle step (usually DOWN) that reads like an odometer clicking over.
 */
type RollMode = "forward" | "nearest";

type ReelProps = {
  /** Target digit (0-9) to settle on. */
  digit: number;
  /** While true the reel rolls continuously. */
  spinning: boolean;
  /** Settle delay (cascades the reels left→right, MSD first). */
  delay: number;
  /** Settle strategy — see `RollMode`. Defaults to "forward". */
  roll?: RollMode;
  /** Previous digit (0-9), used by "nearest" mode to roll the short way from it. */
  from?: number;
};

// Rows rendered per reel. "forward" mode stays in [0,19]; "nearest" mode anchors
// the previous digit at +10 and hops ±5, so it stays in [5,25). 30 rows (showing
// i % 10) cover both windows with margin, so the scrolling strip never exposes a
// blank edge mid-roll.
const REEL_ROWS = 30;

/**
 * A single vertical digit reel. Renders 0-9 twice (so a wrap is seamless)
 * and drives `translateY` imperatively via rAF — no per-frame React renders.
 */
function DigitReel({ digit, spinning, delay, roll = "forward", from }: ReelProps) {
  const stripRef = useRef<HTMLSpanElement | null>(null);
  // Current offset in digit-rows (em units). Kept across renders so a churn →
  // settle hand-off continues from wherever the spin currently is.
  const offsetRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const apply = (o: number) => {
      strip.style.transform = `translateY(${-o}em)`;
    };

    const cancel = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    if (spinning) {
      // Continuous roll, wrapping within [0, 10) — index 10 == index 0.
      let last = performance.now();
      const loop = (now: number) => {
        const dt = (now - last) / 1000;
        last = now;
        let o = offsetRef.current + dt * CHURN_SPEED;
        while (o >= 10) o -= 10;
        offsetRef.current = o;
        apply(o);
        rafRef.current = requestAnimationFrame(loop);
      };
      apply(offsetRef.current);
      rafRef.current = requestAnimationFrame(loop);
      return cancel;
    }

    // "nearest" settle (live countdown) — a single gentle hop from the PREVIOUS
    // digit to the new one, taking the short way (down for a decrement, including
    // the 0→9 borrow). No churn: the reel just clicks over like a real odometer.
    // We anchor the previous digit at +10 so the ±5 hop can never go negative
    // (off the top of the rendered strip), then lock onto the plain digit at rest.
    if (roll === "nearest") {
      const prev =
        typeof from === "number" && Number.isFinite(from)
          ? ((Math.trunc(from) % 10) + 10) % 10
          : ((Math.trunc(digit) % 10) + 10) % 10; // no previous → no movement
      const start = prev + 10;
      const target = nearestSettleTarget(start, digit);
      offsetRef.current = start;
      apply(start);
      const settleStart = performance.now();
      const loop = (now: number) => {
        const t = Math.min(1, (now - settleStart) / SETTLE_MS);
        const o = start + (target - start) * easeOutCubic(t);
        offsetRef.current = o;
        apply(o);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(loop);
        } else {
          offsetRef.current = ((Math.trunc(digit) % 10) + 10) % 10;
          apply(offsetRef.current);
        }
      };
      rafRef.current = requestAnimationFrame(loop);
      return cancel;
    }

    // Settle — two STRICTLY-FORWARD phases so the reel reads like a real car
    // odometer winding down (never reversing, even when the new total is lower):
    //   A) for `delay` ms keep rolling forward at churn speed, so reels that
    //      haven't locked yet keep spinning while the ones to their left settle
    //      (a real odometer winds down left→right — NOT a frozen slot machine).
    //   B) ease-out forward into the next slot that shows `digit`, locking it
    //      "into place" at the end.
    const settleStart = performance.now();
    const phaseAEnd = settleStart + Math.max(0, delay);
    let lastA = settleStart;
    let bComputed = false;
    let bStart = 0;
    let bTarget = 0;

    apply(offsetRef.current);
    const loop = (now: number) => {
      // Phase A — keep churning forward until this reel's turn to lock.
      if (now < phaseAEnd) {
        const dt = (now - lastA) / 1000;
        lastA = now;
        let o = offsetRef.current + dt * CHURN_SPEED;
        while (o >= 10) o -= 10;
        offsetRef.current = o;
        apply(o);
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      // Phase B — ease forward into place. `bTarget >= bStart` always, so the
      // motion can only ever be upward/forward.
      if (!bComputed) {
        bComputed = true;
        bStart = offsetRef.current;
        bTarget = forwardSettleTarget(bStart, digit);
      }
      const t = Math.min(1, (now - phaseAEnd) / SETTLE_MS);
      const o = bStart + (bTarget - bStart) * easeOutCubic(t);
      offsetRef.current = o;
      apply(o);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        offsetRef.current = digit; // bTarget % 10 === digit — lock exactly.
        apply(digit);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return cancel;
  }, [digit, spinning, delay, roll, from]);

  return (
    // The wrapper carries NO `overflow:hidden`, so its baseline is the normal
    // text baseline of the in-flow strut digit below — exactly where the resting
    // plain text sits. The clipped, scrolling reel is overlaid absolutely (out
    // of flow) so it cannot drag the box baseline down to its bottom edge (the
    // old `overflow:hidden` inline-block behaviour, which caused the vertical
    // jump on settle).
    <span
      aria-hidden="true"
      style={{ position: "relative", display: "inline-block", lineHeight: "1em" }}
    >
      {/* Layout strut: reserves the digit's width/height AND establishes the
          text baseline for the whole box. Hidden but in flow. */}
      <span style={{ visibility: "hidden" }}>0</span>
      <span
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "1em",
          lineHeight: "1em",
          overflow: "hidden",
        }}
      >
        <span ref={stripRef} style={{ display: "block", willChange: "transform" }}>
          {Array.from({ length: REEL_ROWS }, (_, i) => (
            <span key={i} style={{ display: "block", height: "1em", lineHeight: "1em" }}>
              {i % 10}
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}

export type RollingNumberProps = {
  value: number | null | undefined;
  loading?: boolean;
  format: (n: number) => string;
  className?: string;
  /** Loading indicator shown in reduced-motion mode while `loading`. */
  fallback?: ReactNode;
  /**
   * Settle strategy (default "forward"):
   *  - "forward": price-total odometer — winds up from a neutral start, with the
   *    left→right stagger and loading churn.
   *  - "nearest": live countdown — each changed digit clicks the short way over
   *    from its previous value (a gentle single step), no churn, no stagger.
   */
  roll?: RollMode;
  "data-testid"?: string;
};

type Phase = "idle" | "churn" | "settle";

export function RollingNumber({
  value,
  loading = false,
  format,
  className,
  fallback,
  roll = "forward",
  "data-testid": testId,
}: RollingNumberProps) {
  const reduced = useReducedMotion();
  const finite = typeof value === "number" && Number.isFinite(value);
  const targetText = finite ? format(value as number) : null;

  // Last value we have actually settled on (drives the idle render).
  const [committed, setCommitted] = useState<string | null>(targetText);
  const [phase, setPhase] = useState<Phase>("idle");
  // The string whose reels we render while animating.
  const [template, setTemplate] = useState<string | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevLoading = useRef<boolean>(loading);

  useEffect(() => {
    if (reduced) {
      // No motion: just keep the committed value in sync.
      if (finite) setCommitted(targetText);
      setPhase("idle");
      return;
    }

    const clearSettle = () => {
      if (settleTimer.current) {
        clearTimeout(settleTimer.current);
        settleTimer.current = null;
      }
    };

    if (loading) {
      clearSettle();
      // Churn against the last committed structure, or a neutral placeholder
      // (currency-correct via format(0)) on the very first load.
      setTemplate((prev) =>
        phase === "churn" && prev ? prev : committed ?? safeFormatZero(format),
      );
      setPhase("churn");
      prevLoading.current = true;
      return;
    }

    // Not loading.
    if (!finite) {
      // Fetch failed / aborted / no value — stop any churn, keep last value.
      clearSettle();
      setPhase("idle");
      prevLoading.current = false;
      return;
    }

    const wasChurning = prevLoading.current || phase === "churn";
    const changed = committed !== targetText;
    prevLoading.current = false;

    if (wasChurning || changed) {
      clearSettle();
      setTemplate(targetText);
      setPhase("settle");
      const digitCount = (targetText ?? "").replace(/\D/g, "").length;
      const total = SETTLE_MS + STAGGER_MS * Math.max(0, digitCount - 1) + 60;
      settleTimer.current = setTimeout(() => {
        setCommitted(targetText);
        setPhase("idle");
        settleTimer.current = null;
      }, total);
    } else {
      setPhase("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, targetText, reduced, finite]);

  useEffect(() => {
    return () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, []);

  // --- Reduced motion: existing loading indicator + plain value. ---
  if (reduced) {
    if (loading) {
      return (
        <span className={className} data-testid={testId} style={NUMERIC_STYLE}>
          {fallback ?? committed ?? safeFormatZero(format)}
        </span>
      );
    }
    return (
      <span className={className} data-testid={testId} style={NUMERIC_STYLE}>
        {committed ?? targetText ?? ""}
      </span>
    );
  }

  // --- Idle / settled: plain text only (clean textContent for tests/SR). ---
  if (phase === "idle") {
    return (
      <span className={className} data-testid={testId} style={NUMERIC_STYLE}>
        {committed ?? targetText ?? fallback ?? ""}
      </span>
    );
  }

  // --- Animating (churn or settle): render reels. ---
  const str = template ?? committed ?? safeFormatZero(format);
  const spinning = phase === "churn";
  // "nearest" mode rolls each reel the short way from its PREVIOUS digit, so we
  // need the prior committed digits positionally aligned with the new ones.
  const prevDigits = roll === "nearest" ? (committed ?? "").replace(/\D/g, "") : "";
  let digitIndex = 0;
  return (
    <span
      className={className}
      data-testid={testId}
      style={NUMERIC_STYLE}
    >
      {/* a11y: announce the resolved value, not the rolling glyphs. */}
      <span className="sr-only">{committed ?? targetText ?? ""}</span>
      <span aria-hidden="true">
        {Array.from(str).map((ch, i) => {
          if (ch >= "0" && ch <= "9") {
            const idx = digitIndex++;
            // Cascade the settle from the most-significant digit (idx 0). The
            // reel ignores `delay` while churning and uses it to stagger the
            // settle so the wheels lock left→right like a real odometer.
            // "nearest" (clock) rolls every reel together (no stagger) one short
            // step from its previous digit, so it reads as a single gentle click.
            const fromChar = prevDigits[idx];
            return (
              <DigitReel
                key={`d-${i}`}
                digit={Number(ch)}
                spinning={spinning}
                delay={roll === "nearest" ? 0 : STAGGER_MS * idx}
                roll={roll}
                from={fromChar != null ? Number(fromChar) : undefined}
              />
            );
          }
          return <span key={`s-${i}`}>{ch}</span>;
        })}
      </span>
    </span>
  );
}

function safeFormatZero(format: (n: number) => string): string {
  try {
    return format(0);
  } catch {
    return "0";
  }
}
